import PropTypes from "prop-types"
import React, { useState, useEffect } from "react"

// Import styles that are safe for both server and client
import "./code-editor.css"

// Create a browser-only component to avoid navigator issues during SSR
const CodeEditorBrowser = ({
  value,
  onChange,
  language = "javascript",
  theme = "healme", // Use our custom theme by default
  placeholder = "Enter code here...",
  height = "300px",
  className = "",
  readOnly = false,
  lineNumbers = true
}) => {
  // Dynamic import of CodeMirror to avoid SSR issues
  const { Controlled: CodeMirror } = require("react-codemirror2")

  // These imports are required for CodeMirror modes and themes
  require("codemirror/lib/codemirror.css")
  require("codemirror/theme/material.css")
  require("codemirror/theme/dracula.css")
  require("codemirror/theme/monokai.css")
  require("codemirror/mode/javascript/javascript")
  require("codemirror/mode/xml/xml")
  require("codemirror/mode/css/css")
  require("codemirror/mode/htmlmixed/htmlmixed")
  require("codemirror/mode/markdown/markdown")
  require("codemirror/addon/edit/closebrackets")
  require("codemirror/addon/edit/closetag")
  require("codemirror/addon/fold/foldcode")
  require("codemirror/addon/fold/foldgutter")
  require("codemirror/addon/fold/brace-fold")
  require("codemirror/addon/fold/xml-fold")
  require("codemirror/addon/fold/indent-fold")
  require("codemirror/addon/fold/markdown-fold")
  require("codemirror/addon/fold/comment-fold")
  require("codemirror/addon/fold/foldgutter.css")
  require("codemirror/addon/hint/show-hint")
  require("codemirror/addon/hint/javascript-hint")
  require("codemirror/addon/hint/html-hint")
  require("codemirror/addon/hint/css-hint")
  require("codemirror/addon/hint/show-hint.css")
  require("codemirror/addon/display/placeholder")
  require("codemirror/addon/scroll/simplescrollbars")
  require("codemirror/addon/scroll/simplescrollbars.css")
  require("codemirror/addon/selection/active-line")

  const [editorValue, setEditorValue] = useState(value || "")

  useEffect(() => {
    setEditorValue(value || "")
  }, [value])

  // Map language string to CodeMirror mode
  const getModeByLanguage = (lang) => {
    switch (lang.toLowerCase()) {
      case "html":
        return "htmlmixed"
      case "css":
        return "css"
      case "javascript":
      case "js":
        return "javascript"
      case "jsx":
        return { name: "javascript", jsx: true }
      case "markdown":
      case "md":
        return "markdown"
      case "xml":
        return "xml"
      default:
        return "javascript"
    }
  }

  const handleChange = (editor, data, value) => {
    setEditorValue(value)
    if (onChange) {
      onChange(value)
    }
  }

  // Check if the theme is one of our supported themes
  const validateTheme = (requestedTheme) => {
    const supportedThemes = ["healme", "dracula", "material", "monokai"]
    return supportedThemes.includes(requestedTheme) ? requestedTheme : "healme"
  }

  return (
    <div
      className={`code-editor-container ${className}`}
      style={{
        height,
        maxHeight: height,
        overflow: "hidden",
        display: "flex",
        flexDirection: "column"
      }}>
      <CodeMirror
        value={editorValue}
        options={{
          mode: getModeByLanguage(language),
          theme: validateTheme(theme),
          lineNumbers: lineNumbers,
          lineWrapping: true,
          autoCloseBrackets: true,
          autoCloseTags: true,
          foldGutter: true,
          gutters: ["CodeMirror-linenumbers", "CodeMirror-foldgutter"],
          extraKeys: { "Ctrl-Space": "autocomplete" },
          placeholder: placeholder,
          readOnly: readOnly,
          tabSize: 2,
          indentWithTabs: false,
          scrollbarStyle: "simple",
          styleActiveLine: true,
          matchBrackets: true,
          viewportMargin: Infinity
        }}
        onBeforeChange={handleChange}
        className="h-full w-full rounded-md font-mono"
      />
    </div>
  )
}

// Main component that checks for browser environment
const CodeEditor = (props) => {
  const [isBrowser, setIsBrowser] = useState(false)

  useEffect(() => {
    setIsBrowser(true)
  }, [])

  // Render fallback for server-side rendering
  if (!isBrowser) {
    return (
      <div
        className={`code-editor-container ${props.className || ""}`}
        style={{
          height: props.height || "300px",
          maxHeight: props.height || "300px",
          overflow: "hidden",
          display: "flex",
          flexDirection: "column",
          border: "1px solid #ccc",
          borderRadius: "4px"
        }}>
        <div>{props.placeholder || "Code editor will load in browser..."}</div>
      </div>
    )
  }

  // Render the browser version of the component
  return <CodeEditorBrowser {...props} />
}

CodeEditor.propTypes = {
  value: PropTypes.string,
  onChange: PropTypes.func,
  language: PropTypes.string,
  theme: PropTypes.string,
  placeholder: PropTypes.string,
  height: PropTypes.string,
  className: PropTypes.string,
  readOnly: PropTypes.bool,
  lineNumbers: PropTypes.bool
}

CodeEditor.defaultProps = {
  language: "javascript",
  theme: "healme",
  placeholder: "Enter code here...",
  height: "300px",
  readOnly: false,
  lineNumbers: true
}

export default CodeEditor
